package fr.manyverse.app.domain.usecase

import fr.manyverse.app.domain.error.ValidationError
import fr.manyverse.app.domain.model.ActiveDigicode
import fr.manyverse.app.domain.model.DigicodeAttempt
import fr.manyverse.app.domain.model.DigicodeStatus
import fr.manyverse.app.domain.repository.DigicodeRepository
import kotlin.time.Clock

/**
 * Use case for submitting a digicode attempt.
 * Compares the value with the solution, determines success/failure,
 * and updates the digicode in the repository.
 */
class SubmitDigicodeAttemptUseCase(
    private val digicodeRepository: DigicodeRepository
) {
    suspend operator fun invoke(
        digicode: ActiveDigicode,
        attemptValue: String
    ): Result<ActiveDigicode> {
        if (digicode.status != DigicodeStatus.ACTIVE) {
            return Result.failure(ValidationError("Digicode is not active"))
        }

        if (!attemptValue.all { it.isDigit() }) {
            return Result.failure(ValidationError("Attempt must contain digits only"))
        }

        if (attemptValue.length != digicode.digitCount) {
            return Result.failure(
                ValidationError("Attempt must have exactly ${digicode.digitCount} digits")
            )
        }

        val isSuccess = attemptValue == digicode.solution
        val newAttemptNumber = digicode.attempts.size + 1
        val newStatus = when {
            isSuccess -> DigicodeStatus.SUCCESS
            newAttemptNumber >= digicode.maxAttempts -> DigicodeStatus.FAILED
            else -> DigicodeStatus.ACTIVE
        }

        val newAttempt = DigicodeAttempt(
            value = attemptValue,
            isSuccess = isSuccess,
            attemptedAt = Clock.System.now()
        )

        val allAttempts = digicode.attempts + newAttempt

        return digicodeRepository.submitAttempt(
            digicodeId = digicode.id,
            newAttempts = allAttempts,
            newStatus = newStatus
        )
    }
}
